import type { BoardDto } from '@mm/shared';
import { useEffect, useState } from 'react';
import { patchAssignment } from '../../api/assignments';
import { cn } from '../../lib/cn';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Input } from '../../ui/Input';
import {
  dueInputValue,
  formatTimeSpent,
  inputToDueIso,
  STATUS_LABEL,
  videoEmbedUrl,
} from './board-ui';

export function TutorAssignmentPanel({
  board,
  onChange,
}: {
  board: BoardDto;
  onChange: (b: BoardDto) => void;
}) {
  const [due, setDue] = useState<string>(dueInputValue(board.dueAt));
  const [hintText, setHintText] = useState<string>(board.hintText ?? '');
  const [hintVideoUrl, setHintVideoUrl] = useState<string>(board.hintVideoUrl ?? '');
  const [tutorComment, setTutorComment] = useState<string>(board.tutorComment ?? '');
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    setDue(dueInputValue(board.dueAt));
    setHintText(board.hintText ?? '');
    setHintVideoUrl(board.hintVideoUrl ?? '');
    setTutorComment(board.tutorComment ?? '');
  }, [board.id]);

  const isAssignment = board.mode === 'ASSIGNMENT';
  const time = formatTimeSpent(board.timeSpentSec);

  async function toggleMode() {
    setSaving('saving');
    try {
      const next = await patchAssignment(board.id, { mode: isAssignment ? 'DRAFT' : 'ASSIGNMENT' });
      onChange(next);
      setSaving('saved');
    } catch {
      setSaving('error');
    }
  }

  async function saveFields() {
    setUrlError(null);
    if (hintVideoUrl.trim() !== '' && !videoEmbedUrl(hintVideoUrl.trim())) {
      setUrlError('Поддерживаются YouTube, Rutube, Vimeo');
      return;
    }
    setSaving('saving');
    try {
      const next = await patchAssignment(board.id, {
        dueAt: inputToDueIso(due),
        hintText: hintText.trim() === '' ? null : hintText,
        hintVideoUrl: hintVideoUrl.trim() === '' ? null : hintVideoUrl.trim(),
        tutorComment: tutorComment.trim() === '' ? null : tutorComment,
      });
      onChange(next);
      setSaving('saved');
    } catch {
      setSaving('error');
    }
  }

  async function markReviewed() {
    setSaving('saving');
    try {
      const next = await patchAssignment(board.id, { status: 'REVIEWED' });
      onChange(next);
      setSaving('saved');
    } catch {
      setSaving('error');
    }
  }

  return (
    <aside className="w-[320px] shrink-0 border-l border-border bg-panel overflow-y-auto flex flex-col">
      <div className="px-5 py-4 border-b border-border">
        <div className="font-caps italic text-[11px] uppercase tracking-[0.12em] text-muted mb-1">
          Задание
        </div>
        <div className="flex items-center justify-between gap-2">
          <span
            className="font-display text-[20px] font-medium leading-none"
            style={{ fontVariationSettings: '"opsz" 48, "SOFT" 10' }}
          >
            {isAssignment ? 'Домашка' : 'Черновик'}
          </span>
          <Button size="sm" variant={isAssignment ? 'outline' : 'default'} onClick={toggleMode}>
            {isAssignment ? 'Снять статус' : 'Сделать домашкой'}
          </Button>
        </div>
        {isAssignment ? (
          <div className="mt-2 flex items-center gap-2 text-[12px]">
            <StatusPill status={board.status} />
            {time ? <span className="text-muted">⌛ {time}</span> : null}
          </div>
        ) : (
          <p className="mt-2 text-[12px] text-muted">
            В режиме домашки ученик видит задание и может отмечать статусы.
          </p>
        )}
      </div>

      {isAssignment ? (
        <div className="px-5 py-4 space-y-4 flex-1">
          <Field label="Срок" hint="Необязательно">
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>

          <Field label="Подсказка — текст" hint="Ученик увидит на боковой панели">
            <textarea
              value={hintText}
              onChange={(e) => setHintText(e.target.value)}
              rows={4}
              className="flex w-full rounded border border-border bg-bg px-3 py-2 text-[13px] text-fg placeholder:text-muted focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring/20 resize-y min-h-[72px]"
              placeholder="Например: «Посмотри разбор задачи №4 из учебника»"
            />
          </Field>

          <Field
            label="Подсказка — видео"
            hint="YouTube, Rutube, Vimeo"
            error={urlError ?? undefined}
          >
            <Input
              type="url"
              value={hintVideoUrl}
              onChange={(e) => setHintVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
            />
          </Field>

          <Field label="Комментарий ученику">
            <textarea
              value={tutorComment}
              onChange={(e) => setTutorComment(e.target.value)}
              rows={3}
              className="flex w-full rounded border border-border bg-bg px-3 py-2 text-[13px] text-fg placeholder:text-muted focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-ring/20 resize-y min-h-[60px]"
              placeholder="Молодец! Обрати внимание на…"
            />
          </Field>

          <div className="flex items-center justify-between gap-2 pt-1">
            <SavingHint state={saving} />
            <Button size="sm" onClick={saveFields} disabled={saving === 'saving'}>
              Сохранить
            </Button>
          </div>

          {board.status === 'FINISHED' ? (
            <div className="border-t border-border-soft pt-4">
              <p className="text-[12px] text-muted mb-2">
                Ученик сообщил, что закончил. Проверь и отметь.
              </p>
              <Button className="w-full" onClick={markReviewed} disabled={saving === 'saving'}>
                Проверил(а)
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

function StatusPill({ status }: { status: BoardDto['status'] }) {
  const tone =
    status === 'REVIEWED'
      ? 'text-accent border-accent/40 bg-accent-soft'
      : status === 'FINISHED'
        ? 'text-fg border-fg/40'
        : status === 'WORKING'
          ? 'text-fg border-border'
          : 'text-muted border-border-soft';
  return (
    <span
      className={cn(
        'inline-block font-caps italic text-[11px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border',
        tone,
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function SavingHint({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'saving')
    return <span className="text-[11px] text-muted italic font-caps">Сохраняем…</span>;
  if (state === 'saved')
    return <span className="text-[11px] text-muted italic font-caps">Сохранено</span>;
  if (state === 'error')
    return <span className="text-[11px] text-accent italic font-caps">Ошибка сохранения</span>;
  return <span />;
}
